import React, { FC, useEffect, useState } from 'react';

import styles from './ContextManagerPage.module.scss';
import { ContextManagerPageProps } from './ContextManagerPageProps';
import { useAppSelector } from 'store/storeHooks';
import { ContextManagerService } from 'pages/ContextManagerPage/ContextManagerService';
import ContextCreationView from 'pages/ContextManagerPage/ContextCreationView/ContextCreationView';
import ContextManagementView from 'pages/ContextManagerPage/ContextManagementView/ContextManagementView';
import { PowerplantContextModel } from 'pages/ContextManagerPage/PowerplantContextModel';
import { PowerplantState } from 'store/powerplant/powerplant-slice';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';


export const ContextManagerPage: FC<ContextManagerPageProps> = () => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [powerplantModel, setPowerplantModel] = useState<PowerplantContextModel | null>(null);

  const powerplant: PowerplantState = useAppSelector(state => state.powerplant);

  useEffect(() => {
    if (powerplant.name) {
      getPowerplantModel(powerplant.name);
    } else {
      setPowerplantModel(null);
    }
  }, [powerplant]);

  const createPowerplantModel = async (powerplantName: string): Promise<void> => {
    setIsFetching(true);
    try {
      const createdPowerplantModel = await ContextManagerService.createPowerplantRDSPSModel(powerplantName);

      if (!createdPowerplantModel) {
        throw new Error('Error creating powerplant context model');
      }

    // TODO: Here, isPolling should have been set to true, which should have triggered an interval of ContextManagerService.getAutomappingStatus().
    //   This would enable the creation of some kind of loading feature, but it needs some work in the backend to work properly
    } catch (error) {
      console.error('Failed creating powerplant context model');
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed creating powerplant context model. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  const getPowerplantModel = async (powerplantName: string): Promise<void> => {
    setIsFetching(true);
    /* TODO: Create an isPolling state, and set it to true here. Have a useEffect triggered by isPolling, and start an
    *   interval polling this "const newStatus = await ContextManagerService.getAutomappingStatus();" and update a
    *   status state that can be displayed as appropriate. Needs some work in the backend for it to work. */
    try {
      const fetchedPowerplantModel = await ContextManagerService.getPowerplantModel(powerplantName);

      if (fetchedPowerplantModel.error) {
        throw new Error('Error getting powerplant context model');
      }

      setPowerplantModel(fetchedPowerplantModel);
    } catch (error) {
      setPowerplantModel(null);
      console.error('Failed getting powerplant context model');
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed getting powerplant context model. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className={styles.ContextManagerPage} data-testid="ContextManagerPage">
      { !powerplant.name && <div>Please select a powerplant</div> }

      { isFetching && <div>L O A D I N G . . . </div>} {/* TODO: Something (ie a loading bar) for when creating a model */}

      { !isFetching && powerplant.name && !powerplantModel && (
        <ContextCreationView
          powerplantName={powerplant.name}
          onCreate={createPowerplantModel}
        />
      )}

      { !isFetching && powerplantModel && powerplant.name && (
        <ContextManagementView
          powerplantModel={powerplantModel}
          powerplantName={powerplant.name}
          onRecreate={createPowerplantModel}
        />
      )}

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
};
